import { Controller } from "@hotwired/stimulus"

// Generic modal controller — open/close with ESC key and backdrop click.
//
// Supports two ways to open:
//
// 1. From within the modal's Stimulus scope:
//    <button data-action="modal#open">Open</button>
//
// 2. From anywhere on the page (cross-controller):
//    <button data-modal-open="company-switcher">Open</button>
//    The modal must have data-modal-id-value="company-switcher".
//
// The modal element should have class="hidden" initially.

export default class extends Controller {
  static targets = ["dialog"]
  static values = { id: String, autoOpen: { type: Boolean, default: false }, dismissable: { type: Boolean, default: true } }

  connect() {
    this._onKeydown = this._onKeydown.bind(this)
    this._onGlobalOpen = this._onGlobalOpen.bind(this)
    this._onProgrammaticOpen = this._onProgrammaticOpen.bind(this)
    document.addEventListener("click", this._onGlobalOpen)
    document.addEventListener("modal:open-by-id", this._onProgrammaticOpen)
    if (this.autoOpenValue) this._open()
  }

  // Triggered by data-modal-open="<id>" clicks anywhere on the page
  _onGlobalOpen(event) {
    const trigger = event.target.closest(`[data-modal-open="${this.idValue}"]`)
    if (trigger) {
      event.preventDefault()
      this._open()
    }
  }

  // Triggered by `document.dispatchEvent(new CustomEvent("modal:open-by-id", { detail: { id } }))`
  _onProgrammaticOpen(event) {
    if (event.detail?.id === this.idValue) this._open()
  }

  open(event) {
    if (event) event.preventDefault()
    this._open()
  }

  close(event) {
    if (!this.dismissableValue) return
    if (event) event.preventDefault()
    this._close()
  }

  // Track where mousedown started so drags from inside don't close the modal
  backdropMousedown(event) {
    this._mousedownTarget = event.target
  }

  // Close on backdrop click (click on the overlay itself, not the dialog)
  closeOnBackdrop(event) {
    if (!this.dismissableValue) return
    if (event.target === this.element && this._mousedownTarget === this.element) {
      this._close()
    }
    this._mousedownTarget = null
  }

  _open() {
    // Close any open dropdowns before showing the modal
    document.dispatchEvent(new CustomEvent("modal:open"))
    this.element.classList.remove("hidden")
    document.addEventListener("keydown", this._onKeydown)
    document.body.style.overflow = "hidden"
    // Scoped event for child controllers (e.g. lazy-content) that need
    // to react when this specific modal opens. Bubbles from this.element.
    this.dispatch("opened")
  }

  _close() {
    this.element.classList.add("hidden")
    document.removeEventListener("keydown", this._onKeydown)
    document.body.style.overflow = ""
  }

  _onKeydown(event) {
    if (event.key === "Escape" && this.dismissableValue) {
      this._close()
    }
  }

  disconnect() {
    document.removeEventListener("click", this._onGlobalOpen)
    document.removeEventListener("modal:open-by-id", this._onProgrammaticOpen)
    document.removeEventListener("keydown", this._onKeydown)
    document.body.style.overflow = ""
  }
}
